import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Dialog, Grid, styled } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import { StaticQuery, graphql } from 'gatsby';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { novadeLogo } from '../assets/icons';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { NovadeLiteUrlContext } from '../contexts/NovadeLiteUrlContext';
import { globalColors } from '../styles/globalColorVariables';
import { LocationLanguageContextType, LogInResponse, NovadeLiteUrlContextType } from '../typings';
import { Languages } from '../utils/getAllSupportedLanguages';
import { getMarkdownValue } from '../utils/getMarkdownValue';
import { matchesMobile } from '../utils/mediaQuery';
import { selectLocalizedData } from '../utils/selectLocalizedData';
import { setGTMEvent } from '../utils/setGAEvent';
import DialogCloseButton from './Dialog';

interface StrapiLoginDialog {
  description: string;
  email: string;
  login: string;
  password: string;
  error: string;
  locale: string;
}

interface LoginDialogQueryProps {
  allStrapiLoginDialog: {
    nodes: StrapiLoginDialog[];
  };
}

interface LoginDialogProps {
  open: boolean;
  onDialogClose: (value?: LogInResponse) => void;
}

interface LoginFormState {
  email: string;
  password: string;
  showPassword: boolean;
  loading: boolean;
  isEmailInvalid: boolean;
  error: string;
}

const MarkdownText = styled('div')({
  '& a': {
    textDecoration: 'none',
    color: globalColors.primaryColor
  },
  textAlign: 'center'
});

const NOVADE_LITE_LOGIN_URL_SEA = process.env.GATSBY_NOVADE_LITE_API_URL_SEA;
const NOVADE_LITE_LOGIN_URL_EU = process.env.GATSBY_NOVADE_LITE_API_URL_EU;
const NOVADE_LITE_LOGIN_URL_AUZ = process.env.GATSBY_NOVADE_LITE_API_URL_AUZ;

const LoginDialog: React.FC<LoginDialogProps> = ({ onDialogClose, open }) => (
  <StaticQuery
    query={graphql`
      query LoginDialogQuery {
        allStrapiLoginDialog {
          nodes {
            description {
              data {
                description
              }
            }
            email
            login
            password
            error
            locale
            localizations {
              email
              login
              password
              error
              locale
              description
            }
          }
        }
      }
    `}
    render={(data: LoginDialogQueryProps) => {
      const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
      const language: string = locationLanguage?.language ?? Languages.English;
      const loginDialogContent = selectLocalizedData(data.allStrapiLoginDialog.nodes[0], language);

      const initialValues = {
        email: '',
        password: '',
        showPassword: false,
        isEmailInvalid: false,
        loading: false,
        error: ''
      };
      const [values, setValues] = React.useState<LoginFormState>(initialValues);

      const handleClose = () => {
        onDialogClose();
      };

      const handleChange = (prop: keyof LoginFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues({ ...values, [prop]: event.target.value });
      };
      const handleClickShowPassword = () => {
        setValues({ ...values, showPassword: !values.showPassword });
      };
      const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
      };
      const isEmailValid = (email: string) => {
        if (email === '') {
          setValues({ ...values, isEmailInvalid: false });
        }
        const re =
          // eslint-disable-next-line max-len
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (re.test(email)) {
          setValues({ ...values, isEmailInvalid: false });
        } else {
          setValues({ ...values, isEmailInvalid: true });
        }
      };

      const novadeLiteAPI = React.useContext<NovadeLiteUrlContextType>(NovadeLiteUrlContext);

      async function login(url: string) {
        const response = await fetch(`${url}/login`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            email: values.email,
            password: values.password
          })
        });

        return response;
      }

      async function submitForm() {
        setValues({ ...values, loading: true });

        const [resSEA, resEU, resAUZ] = await Promise.all([
          login(NOVADE_LITE_LOGIN_URL_SEA!),
          login(NOVADE_LITE_LOGIN_URL_EU!),
          login(NOVADE_LITE_LOGIN_URL_AUZ!)
        ]);

        let logMessage = '';
        let endpoint = '';
        let response: any = {};

        if (resSEA.ok) {
          response = await resSEA.json();
          endpoint = NOVADE_LITE_LOGIN_URL_SEA!;
          logMessage = 'Login with Novade Lite SEA credentials';
        } else if (resEU.ok) {
          response = await resEU.json();
          endpoint = NOVADE_LITE_LOGIN_URL_EU!;
          logMessage = 'Login with Novade Lite EU credentials';
        } else if (resAUZ.ok) {
          response = await resAUZ.json();
          endpoint = NOVADE_LITE_LOGIN_URL_AUZ!;
          logMessage = 'Login with Novade Lite AUZ credentials';
        } else {
          setValues({ ...values, loading: false, error: loginDialogContent ? loginDialogContent.error : 'Error submitting form' });
          return;
        }

        const content: LogInResponse = {
          email: values.email,
          userID: response.userID,
          userDisplayName: response.userDisplayName,
          userPhotoID: response.photoID,
          databases: { [endpoint]: response.databases }
        };

        setValues(initialValues); // Clear email / password from login form
        novadeLiteAPI?.updateNovadeLiteUrl(endpoint);
        onDialogClose(content);
        setGTMEvent('login', 'login', logMessage);
      }
      const isMobile = matchesMobile();

      if (!loginDialogContent) return null;

      return (
        <Dialog fullScreen={isMobile} maxWidth='xs' fullWidth onClose={handleClose} aria-labelledby='login-dialog' open={open}>
          <DialogCloseButton handleClose={handleClose} />
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ marginBottom: '1rem' }}>
              <img src={novadeLogo} alt='Novade Lite logo' />
            </Box>
            <Grid container justifyContent='center' alignItems='center' sx={{ padding: '0 2rem' }}>
              <FormControl variant='outlined' fullWidth sx={{ marginBottom: '1rem' }}>
                <InputLabel htmlFor='outlined-email'>{loginDialogContent.email}</InputLabel>
                <OutlinedInput
                  id='outlined-email'
                  type='email'
                  value={values.email}
                  error={values.isEmailInvalid}
                  onBlur={() => isEmailValid(values.email)}
                  label={loginDialogContent.email}
                  onChange={handleChange('email')}
                />
              </FormControl>
              <FormControl variant='outlined' fullWidth sx={{ marginBottom: '1rem' }}>
                <InputLabel htmlFor='outlined-adornment-password'>{loginDialogContent.password}</InputLabel>
                <OutlinedInput
                  id='outlined-adornment-password'
                  type={values.showPassword ? 'text' : 'password'}
                  value={values.password}
                  onChange={handleChange('password')}
                  label={loginDialogContent.password}
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle password visibility'
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge='end'
                      >
                        {values.showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
              {values.error !== '' && (
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <Typography variant='caption' color='error' align='center'>
                    {values.error}
                  </Typography>
                </div>
              )}
              <Button
                disabled={values.loading}
                size='large'
                fullWidth
                variant='contained'
                onClick={() => submitForm()}
                color='secondary'
                style={{ textTransform: 'uppercase', color: 'white', marginBottom: '1rem' }}
              >
                {!values.loading ? (
                  loginDialogContent.login
                ) : (
                  <div>
                    <CircularProgress style={{ height: '1rem', width: '1rem', color: 'white' }} />
                  </div>
                )}
              </Button>
              <Typography component='div'>
                <MarkdownText>
                  <ReactMarkdown>{getMarkdownValue(loginDialogContent.description)}</ReactMarkdown>
                </MarkdownText>
              </Typography>
            </Grid>
          </DialogContent>
        </Dialog>
      );
    }}
  />
);

export default LoginDialog;
